import type { BoundPayRun, PayGroupOption, PayRunState } from "../../types/matching";

export const MOCK_PAY_GROUPS: PayGroupOption[] = [
  { payGroupId: "PG-MAIN", payGroupName: "Main fortnightly" },
  { payGroupId: "PG-WEEKLY", payGroupName: "Weekly" },
  { payGroupId: "WEEKLY", payGroupName: "WEEKLY" },
];

/** Curated demos (optional — any other non-empty id also resolves in mock). */
const CATALOGUE: Record<string, BoundPayRun> = {
  "PR-2026-WEEKLY-11OCT": {
    payRunId: "PR-2026-WEEKLY-11OCT",
    payGroupId: "WEEKLY",
    payGroupName: "WEEKLY",
    periodEnd: "2026-10-11",
    state: "In Progress",
  },
  "PR-2026-WEEKLY-PENDING": {
    payRunId: "PR-2026-WEEKLY-PENDING",
    payGroupId: "WEEKLY",
    payGroupName: "WEEKLY",
    periodEnd: "2026-10-11",
    state: "Pending",
  },
  "PR-2026-WEEKLY-CLOSED": {
    payRunId: "PR-2026-WEEKLY-CLOSED",
    payGroupId: "WEEKLY",
    payGroupName: "WEEKLY",
    periodEnd: "2026-10-11",
    state: "Finalised",
  },
  "PR-2026-WEEKLY-AUTH": {
    payRunId: "PR-2026-WEEKLY-AUTH",
    payGroupId: "WEEKLY",
    payGroupName: "WEEKLY",
    periodEnd: "2026-10-11",
    state: "Authorised",
  },
  "PR-2026-WEEKLY-RA": {
    payRunId: "PR-2026-WEEKLY-RA",
    payGroupId: "WEEKLY",
    payGroupName: "WEEKLY",
    periodEnd: "2026-10-11",
    state: "Requires Authorisation",
  },
};

/** Legacy shape: `PR-{YYYY-MM-DD}-{payGroupId}`. */
const PAY_RUN_ID_RE = /^PR-(\d{4}-\d{2}-\d{2})-(.+)$/;

function stateForPeriodEnd(periodEnd: string): PayRunState {
  if (periodEnd.endsWith("-15")) return "Closed";
  if (periodEnd.endsWith("-28")) return "Finalised";
  if (periodEnd.endsWith("-01")) return "Pending";
  return "In Progress";
}

/** Blocked-state sentinels for free-form mock ids (token match, not substring). */
function stateFromFreeFormId(id: string): PayRunState {
  const tokens = new Set(
    id
      .toUpperCase()
      .split(/[^A-Z0-9]+/)
      .filter(Boolean)
  );
  if (tokens.has("AUTH") || id.toUpperCase().endsWith("-A")) return "Authorised";
  if (tokens.has("RA") || tokens.has("REQUIRES")) {
    return "Requires Authorisation";
  }
  if (tokens.has("FINAL") || tokens.has("FINALISED") || tokens.has("CLOSED")) {
    return "Finalised";
  }
  if (tokens.has("PENDING")) return "Pending";
  return "In Progress";
}

function periodEndFromFreeFormId(id: string): string {
  // Stable-ish mock period from last digits, else a fixed demo PPE.
  const digits = id.replace(/\D/g, "");
  if (digits.length >= 4) {
    const n = Number(digits.slice(-4));
    const day = String((n % 28) + 1).padStart(2, "0");
    return `2026-10-${day}`;
  }
  return "2026-10-11";
}

export function mockResolvePayRun(
  periodEnd: string,
  payGroupId: string
): BoundPayRun | null {
  const group = MOCK_PAY_GROUPS.find((g) => g.payGroupId === payGroupId);
  if (!group) return null;
  return {
    payRunId: `PR-${periodEnd}-${payGroupId}`,
    payGroupId: group.payGroupId,
    payGroupName: group.payGroupName,
    periodEnd,
    state: stateForPeriodEnd(periodEnd),
  };
}

/**
 * Local mock resolve — accepts **any** non-empty pay run ID so Option A is
 * tryable without Christmas. Use `NOTFOUND` to force a miss; include AUTH /
 * CLOSED / PENDING in the id to demo blocked/writable states.
 */
export function mockResolveByPayRunId(payRunId: string): BoundPayRun | null {
  const normalized = payRunId.trim();
  if (!normalized || normalized.toUpperCase() === "NOTFOUND") return null;
  if (CATALOGUE[normalized]) return CATALOGUE[normalized];

  const legacy = PAY_RUN_ID_RE.exec(normalized);
  if (legacy) {
    const resolved = mockResolvePayRun(legacy[1], legacy[2]);
    if (resolved) return resolved;
  }

  const periodEnd = periodEndFromFreeFormId(normalized);
  return {
    payRunId: normalized,
    payGroupId: "PG-MAIN",
    payGroupName: "Main fortnightly",
    periodEnd,
    state: stateFromFreeFormId(normalized),
  };
}
